import { useEffect, useMemo, useState } from 'react';

import { Button, Group, Select, Text, TextInput } from '@mantine/core';

import { useGetLeadChambersQuery } from '@/store/slices/api/patientsApiSlice';
import { CHAMBER_ADD_NEW, CHAMBER_OTHER, chamberPicklistOptions } from '@/utils/leadChambers';

import styles from './LeadChamberSelect.module.scss';

type LeadChamberSelectProps = {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  size?: 'xs' | 'sm' | 'md';
  placeholder?: string;
};

export const LeadChamberSelect = ({
  value,
  onChange,
  disabled,
  label = 'Chamber',
  size = 'xs',
  placeholder = 'Select chamber'
}: LeadChamberSelectProps) => {
  const { data: stored = [] } = useGetLeadChambersQuery();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    setSearch(value?.trim() ?? '');
  }, [value]);

  const data = useMemo(
    () => chamberPicklistOptions({ stored, current: value, search }),
    [stored, value, search]
  );

  const saveDraft = () => {
    const next = draft.trim();
    if (!next) return;
    onChange(next);
    setDraft('');
    setAdding(false);
  };

  if (adding) {
    return (
      <div>
        <TextInput
          classNames={{ root: styles.field }}
          label={label}
          size={size}
          value={draft}
          placeholder="New chamber"
          disabled={disabled}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              saveDraft();
            }
          }}
        />
        <Group gap={6} mt={6}>
          <Button size="xs" color="dark" disabled={disabled || !draft.trim()} onClick={saveDraft}>
            Save
          </Button>
          <Button
            size="xs"
            variant="default"
            disabled={disabled}
            onClick={() => {
              setDraft('');
              setSearch(value?.trim() ?? '');
              setAdding(false);
            }}>
            Cancel
          </Button>
        </Group>
      </div>
    );
  }

  return (
    <Select
      classNames={{ root: styles.field, dropdown: styles.menu, option: styles.option }}
      label={label}
      size={size}
      data={data}
      value={value?.trim() || null}
      renderOption={({ option }) => (
        <Text
          size="sm"
          c="#1a1a2e"
          className={
            option.value === CHAMBER_OTHER
              ? `${styles.footer} ${styles.divider}`
              : option.value === CHAMBER_ADD_NEW
                ? styles.footer
                : undefined
          }>
          {option.label}
        </Text>
      )}
      onChange={(next) => {
        if (next === CHAMBER_ADD_NEW) {
          setDraft(search.trim() === (value?.trim() ?? '') ? '' : search.trim());
          setAdding(true);
          return;
        }
        onChange(next === CHAMBER_OTHER ? CHAMBER_OTHER : (next ?? ''));
      }}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      allowDeselect={false}
      disabled={disabled}
      placeholder={placeholder}
      maxDropdownHeight={280}
      comboboxProps={{ withinPortal: true }}
      filter={({ options, search: query }) => {
        const needle = query.trim().toLowerCase();
        return options.filter((option) => {
          if (!('value' in option)) return false;
          const optionLabel = String(option.label).toLowerCase();
          const optionValue = String(option.value).toLowerCase();
          return !needle || optionLabel.includes(needle) || optionValue.includes(needle);
        });
      }}
    />
  );
};
